import { createQueryClient, NOT_AUTHORIZED_MESSAGE, toast } from '@h2hspark/ui';
import { QueryClientProvider } from '@tanstack/react-query';
import * as React from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { Toaster } from 'sonner';

import '@h2hspark/auth-ui/styles.css';
import './styles/globals.css';

import { App } from './App';
import { AuthProvider } from './auth/auth-provider';

const rootElement = document.getElementById('root');
if (rootElement === null) {
  throw new Error('Could not find application root element.');
}

const queryClient = createQueryClient({
  // **A 403 is not a session ending, so it does not sign anybody out.** Their token is fine and
  // they will land back on this screen and be refused again. Saying so is the whole remedy —
  // without it a refused read left the page on its loading skeletons and said nothing at all.
  onForbidden: () => {
    // **One message, however many cards refused.** A dashboard whose every query is refused
    // raised one toast per query — three identical notices stacked in a corner, none of them
    // actionable. A stable id collapses them into one.
    //
    // The transport has already asked `/auth/me` to re-verify the session by the time this runs
    // (see `createApiRequest`): if it has ended, the redirect supersedes this message; if it is
    // alive, the refusal really was about permission and this is the right thing to say.
    toast.error(NOT_AUTHORIZED_MESSAGE, { id: 'not-authorised' });
  },
});

createRoot(rootElement).render(
  <React.StrictMode>
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <AuthProvider>
          <App />
          <Toaster richColors closeButton position="top-center" />
        </AuthProvider>
      </BrowserRouter>
    </QueryClientProvider>
  </React.StrictMode>,
);
